import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ExamdojoAgGridModule } from '@examdojo/ag-grid';
import { ButtonComponent } from '@examdojo/core/button';
import { DateTimeFormat, DateTimeService } from '@examdojo/core/date-time';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { SingleSelectComponent } from '@examdojo/core/select';
import { ToastService } from '@examdojo/core/toast';
import { IonContent, IonFooter, IonHeader, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import {
  CellClassParams,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { tap } from 'rxjs';
import { DailyHoursMap, MAX_DAILY_HOURS, WorkingHoursEntry } from '../payments.model';
import { PayoutRequestQuery } from '../payout-request/payout-request.query';
import { WorkingHoursDialogResult, WorkingHoursDialogService } from './working-hours-dialog.service';

@Component({
  selector: 'dojo-working-hours-dialog',
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonFooter,
    IonContent,
    ButtonComponent,
    IconComponent,
    IconButtonComponent,
    SingleSelectComponent,
    ExamdojoAgGridModule,
    ReactiveFormsModule,
    CurrencyPipe,
  ],
  templateUrl: './working-hours-dialog.component.html',
  styleUrl: './working-hours-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'working-hours-dialog flex flex-col h-full' },
})
export class WorkingHoursDialogComponent implements OnInit {
  constructor() {
    effect(() => {
      const period = this.period();
      const existingHours = this.existingHours();

      this.initializeWorkingHoursData(period, existingHours);
    });
  }

  private static readonly AVAILABLE_MONTHS_COUNT = 3;
  private static readonly EARLIEST_SUBMISSION_DAYS_AFTER_PERIOD = 3;

  private readonly workingHoursDialogService = inject(WorkingHoursDialogService);
  private readonly toastService = inject(ToastService);
  private readonly dateTimeService = inject(DateTimeService);
  private readonly payoutRequestQuery = inject(PayoutRequestQuery);
  private readonly destroyRef = inject(DestroyRef);

  readonly period = input.required<string>();
  readonly existingHours = input.required<DailyHoursMap>();
  readonly isReadonly = input.required<boolean>();
  readonly isExistingRequest = input.required<boolean>();
  readonly hourlyPaymentRate = input.required<number>();
  readonly existingRequestPeriods = input.required<string[]>();

  readonly periodControl = new FormControl<string>('');
  readonly workingHoursData = signal<WorkingHoursEntry[]>([]);
  private gridApi: GridApi<WorkingHoursEntry> | null = null;

  readonly availablePeriods = computed(() => {
    const periods: Array<{ id: string; name: string }> = [];
    const today = new Date();
    const existingPeriods = this.existingRequestPeriods();
    const isEditingExisting = this.isExistingRequest();

    if (isEditingExisting) {
      const currentPeriod = this.period();
      const [year, month] = currentPeriod.split('-').map(Number);
      const date = new Date(year, month - 1, 1);
      const displayName = this.dateTimeService.formatDate(date, DateTimeFormat.LongMonthYear);
      periods.push({ id: currentPeriod, name: displayName });
    } else {
      for (let i = 0; i < WorkingHoursDialogComponent.AVAILABLE_MONTHS_COUNT; i++) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const periodStr = this.dateTimeService.__format(date, 'yyyy-MM');

        if (!existingPeriods.includes(periodStr)) {
          const displayName = this.dateTimeService.formatDate(date, DateTimeFormat.LongMonthYear);
          periods.push({ id: periodStr, name: displayName });
        }
      }
    }

    return periods;
  });

  readonly totalHours = computed(() => this.workingHoursData().reduce((total, entry) => total + entry.hours, 0));

  readonly originalTotalHours = computed(() =>
    Object.values(this.existingHours()).reduce((total, entry) => total + entry, 0),
  );

  readonly estimatedPayout = computed(() => this.totalHours() * this.hourlyPaymentRate());

  readonly hasChanges = computed(() => {
    const originalHours = this.existingHours();
    return this.workingHoursData().some((entry) => {
      const originalValue = originalHours[entry.date] ?? 0;
      return entry.hours !== originalValue;
    });
  });

  readonly canSubmitForPeriod = computed(() => {
    const period = this.periodControl.value;
    if (!period) {
      return false;
    }
    return this.canSubmitForPeriodString(period);
  });

  readonly columnDefs = computed<Array<ColDef<WorkingHoursEntry>>>(() => [
    {
      headerName: 'Date',
      field: 'date',
      width: 100,
      cellRenderer: (params: ValueGetterParams<WorkingHoursEntry>) => {
        if (!params.data?.date) {
          return '';
        }
        const date = this.dateTimeService.parseDate(params.data.date);
        const formattedDate = this.dateTimeService.__format(date, 'do MMM yy');
        const fullDayName = this.dateTimeService.__format(date, 'EEEE');
        return `<div class="flex gap-2"><span class="inline-block w-[88px]">${formattedDate}</span><span class="text-gray-500">-</span><span>${fullDayName}</span></div>`;
      },
      cellClass: (params: CellClassParams<WorkingHoursEntry>) => {
        if (!params.data?.date) {
          return '';
        }
        const date = this.dateTimeService.parseDate(params.data.date);
        const dayOfWeek = date.getDay();
        return dayOfWeek === 0 || dayOfWeek === 6 ? 'weekend-cell' : '';
      },
    },
    {
      headerName: this.isReadonly() ? 'Hours' : 'Hours (double click to edit)',
      field: 'hours',
      width: 100,
      editable: (_params) => !this.isReadonly(),
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        min: 0,
        max: MAX_DAILY_HOURS,
        precision: 1,
      },
      valueGetter: (params: ValueGetterParams<WorkingHoursEntry>) => {
        return params.data?.hours || 0;
      },
      valueSetter: (params: ValueSetterParams<WorkingHoursEntry>) => {
        if (!params.data || !this.gridApi) {
          return false;
        }

        const hours = Math.max(0, Math.min(MAX_DAILY_HOURS, parseFloat(params.newValue) || 0));
        const updatedEntry = { ...params.data, hours };

        this.workingHoursData.update((data) =>
          data.map((entry) => (entry.date === updatedEntry.date ? updatedEntry : entry)),
        );

        this.gridApi.applyTransaction({ update: [updatedEntry] });

        return true;
      },
      cellClass: (params: CellClassParams<WorkingHoursEntry>) => {
        const hours = params.data?.hours || 0;
        const classes = [hours === 0 ? 'no-hours-cell' : 'normal-hours-cell hours-logged-cell'];
        if (!this.isReadonly()) {
          classes.push('editable-cell');
        }
        return classes.join(' ');
      },
    },
  ]);

  readonly gridOptions: GridOptions<WorkingHoursEntry> = {
    rowSelection: 'single',
    suppressRowClickSelection: true,
    suppressMovableColumns: true,
    suppressMenuHide: true,
    suppressContextMenu: true,
    enableCellTextSelection: true,
    rowHeight: 40,
    headerHeight: 40,
    stopEditingWhenCellsLoseFocus: true,
    getRowId: (params) => params.data.date,
    defaultColDef: {
      resizable: false,
      sortable: false,
      menuTabs: [],
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
  };

  ngOnInit(): void {
    const availablePeriods = this.availablePeriods();
    const initialPeriod = availablePeriods.length > 0 ? availablePeriods[0].id : this.period();

    this.periodControl.setValue(initialPeriod);

    this.periodControl.valueChanges
      .pipe(
        tap((period) => {
          if (period) {
            this.loadHoursForPeriod(period);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onGridReady(params: GridReadyEvent<WorkingHoursEntry>): void {
    this.gridApi = params.api;
    params.api.setGridOption('rowData', this.workingHoursData());
    params.api.sizeColumnsToFit();
  }

  getPeriodDisplayName(period: string): string {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return this.dateTimeService.formatDate(date, DateTimeFormat.LongMonthYear);
  }

  dismiss(): void {
    this.workingHoursDialogService.dismiss();
  }

  saveDraft(): void {
    const result: WorkingHoursDialogResult = {
      period: this.periodControl.value!,
      hours: this.getHoursFromGrid(),
      shouldSubmit: false,
    };
    this.workingHoursDialogService.dismiss(result);
  }

  submitHours(): void {
    if (!this.validateHours()) {
      return;
    }

    const result: WorkingHoursDialogResult = {
      period: this.periodControl.value!,
      hours: this.getHoursFromGrid(),
      shouldSubmit: true,
    };
    this.workingHoursDialogService.dismiss(result);
  }

  private loadHoursForPeriod(period: string): void {
    const allRequests = this.payoutRequestQuery.getAllUIEntities();
    const request = allRequests.find((r) => r.period === period);
    const existingHours = request?.hours ?? {};
    this.initializeWorkingHoursData(period, existingHours);
  }

  private initializeWorkingHoursData(period: string, existingHours: DailyHoursMap): void {
    const [year, month] = period.split('-').map(Number);
    const daysInMonth = this.dateTimeService.endOfMonth(new Date(year, month - 1, 1)).getDate();

    const workingHoursData: WorkingHoursEntry[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month - 1, day);
      const dateString = this.dateTimeService.__format(date, 'yyyy-MM-dd');

      workingHoursData.push({
        date: dateString,
        dayName: this.dateTimeService.__format(date, 'EEE'),
        hours: existingHours[dateString] || 0,
      });
    }

    this.workingHoursData.set(workingHoursData);

    if (this.gridApi) {
      this.gridApi.setGridOption('rowData', workingHoursData);
    }
  }

  private getHoursFromGrid(): DailyHoursMap {
    const hoursMap: DailyHoursMap = {};

    this.workingHoursData().forEach((entry) => {
      if (entry.hours > 0) {
        hoursMap[entry.date] = entry.hours;
      }
    });

    return hoursMap;
  }

  private validateHours(): boolean {
    const totalHours = this.totalHours();

    if (totalHours === 0) {
      this.toastService.error('Please enter at least some working hours before submitting');
      return false;
    }

    const invalidDays = this.workingHoursData().filter((entry) => entry.hours > MAX_DAILY_HOURS);
    if (invalidDays.length > 0) {
      this.toastService.error(`Maximum ${MAX_DAILY_HOURS} hours per day allowed`);
      return false;
    }

    return true;
  }

  private canSubmitForPeriodString(period: string): boolean {
    const earliestDate = this.getEarliestAllowedDate(period);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today >= earliestDate;
  }

  private getEarliestAllowedDate(period: string): Date {
    const periodDate = this.dateTimeService.parseDate(period, 'yyyy-MM');
    const lastDayOfMonth = this.dateTimeService.endOfMonth(periodDate);
    const earliestDate = this.dateTimeService.add(lastDayOfMonth, {
      days: WorkingHoursDialogComponent.EARLIEST_SUBMISSION_DAYS_AFTER_PERIOD,
    });
    return this.dateTimeService.startOfDay(earliestDate);
  }
}
